/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, July 7th 2022, 12:10:12 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import ListItemButton from "@mui/material/ListItemButton";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import DisabledIcon from "@mui/icons-material/NotInterested";
import { ProcessStage } from "lib/schemas";
import { APIListProgressWrapper } from "ui-widgets";
import { useListProcessStagesAPI } from "service-builder/services/ProcessStageAPI";
import { useServiceBuilderContext } from "service-builder/providers";
import { StageIcons } from "./StageWidgets";
import { StageFormButtonWrapper } from "./StageFormButtonWrapper";
import { EmptyStagesCTA } from "./EmptyStagesCTA";

interface WorkflowStagesListProps {
  handleFilterByStage: (processStageId: ProcessStage["id"]) => void;
  selectedProcessStageId: ProcessStage["id"];
  onEditStagesList: () => void;
}
export const WorkflowStagesList = (props: WorkflowStagesListProps) => {
  const { processStages, isAPIError, isMakingAPIRequest, handleSelectProcessStage, isSelectedStage } =
    useWorkflowStagesList(props);

  return (
    <div style={{ paddingLeft: 5, paddingRight: 5, paddingTop: 5, paddingBottom: 5, minWidth: "300px" }}>
      <APIListProgressWrapper
        items={processStages}
        isAPIError={isAPIError}
        showLoader={isMakingAPIRequest}
        loaderType="linear"
        noDataMessage={<EmptyStagesCTA onCreateNewStage={props.onEditStagesList} />}
      >
        {processStages.map((processStage) => (
          <StageItemLayout
            key={`workflow-stage-list-item-${processStage.id}`}
            selected={isSelectedStage(processStage.id)}
            handleSelectProcessStage={handleSelectProcessStage}
            processStage={processStage}
            onEditStage={props.onEditStagesList}
          />
        ))}
        <AddNewStageButton onCreateNewStage={props.onEditStagesList} />
      </APIListProgressWrapper>
    </div>
  );
};

const useWorkflowStagesList = ({
  handleFilterByStage,
  selectedProcessStageId,
  onEditStagesList,
}: WorkflowStagesListProps) => {
  const {
    processDefinitionContext: { currentProcessDefinition },
  } = useServiceBuilderContext();

  const {
    fetchList,
    isAPIError,
    isMakingAPIRequest,
    items: processStages,
  } = useListProcessStagesAPI({
    processDefinitionId: currentProcessDefinition.id,
  });

  React.useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectProcessStage = (processStageId: ProcessStage["id"]) => {
    if (processStageId === selectedProcessStageId) {
      handleFilterByStage(null);
      return;
    }
    handleFilterByStage(processStageId);
  };

  const isSelectedStage = (processStageId: ProcessStage["id"]) => processStageId === selectedProcessStageId;

  return { processStages, isAPIError, isMakingAPIRequest, handleSelectProcessStage, isSelectedStage };
};

interface StageItemLayoutProps {
  selected: boolean;
  processStage: ProcessStage;
  handleSelectProcessStage: (processStageId: ProcessStage["id"]) => void;
  onEditStage: () => void;
}

const StageItemLayout = ({ selected, handleSelectProcessStage, processStage, onEditStage }: StageItemLayoutProps) => {
  const handleOnClickStage = (event: React.MouseEvent<HTMLDivElement>) => handleSelectProcessStage(processStage.id);

  return (
    <Tooltip title={processStage.active ? "" : <Typography>Stage is inactive</Typography>} arrow placement="right-end">
      <Stack direction="row" className={`workflow-stage-list-item ${processStage.active ? "active" : "inactive"}`}>
        <ListItemButton selected={selected} onClick={handleOnClickStage} disableRipple>
          <Grid container>
            <Grid item xs={2}>
              {StageIcons[processStage.stage_type]}
            </Grid>
            <Grid item xs={10}>
              <Stack direction="row" spacing={2}>
                {!processStage.active && <DisabledIcon color="warning" />}
                <Typography>{processStage.stage_name}</Typography>
              </Stack>
            </Grid>
          </Grid>
        </ListItemButton>
        <StageFormButtonWrapper processStageFetchId={processStage.id} onSuccessfulAPISave={onEditStage}>
          <IconButton color="primary" sx={{ borderRadius: 1 }}>
            <EditIcon />
          </IconButton>
        </StageFormButtonWrapper>
      </Stack>
    </Tooltip>
  );
};

const AddNewStageButton = ({ onCreateNewStage }) => {
  return (
    <StageFormButtonWrapper onSuccessfulAPISave={onCreateNewStage}>
      <ListItemButton disableRipple>
        <Grid container>
          <Grid item xs={2}>
            <AddIcon color="primary" />
          </Grid>
          <Grid item xs={10}>
            <Typography>Add New Stage</Typography>
          </Grid>
        </Grid>
      </ListItemButton>
    </StageFormButtonWrapper>
  );
};
